// node modules
import { autorun } from 'mobx';
// modules
import FetchStore from 'modules/Fetch';
import AuthService from 'modules/Auth';
import AnalyticsService from 'modules/Analytics/service';
import LoggerService from 'modules/Logger';
import RoutingService from 'modules/Routing';
import { CleverTapService } from 'modules/CleverTap';
import { EVENTS } from 'modules/Analytics/consts';
import { CookieStorage } from 'modules/Storage';
import Validation from 'modules/Validation';
import LocaleService from 'modules/Localization';
// typings
import { SegmentType } from 'modules/Search/constants';
import { PaymentMode, PaymentModeActivationStatus, ProfileResponse, UserPreferenceSettings } from './typings';
// store
import UserStore from './store';

class UserService extends UserStore {
  profileRequest = async () => {
    if (!this.isActiveUser) return;

    try {
      const profileData = await FetchStore.request<'PROFILE', ProfileResponse>(
        'PROFILE',
        '/client-info',
        'PROFILE',
        {
          withDeviceDetails: true,
        },
      );

      if (profileData.success) {
        this.handleSuccessGetProfileResponse(profileData.data);
      } else if (CookieStorage.getItem('user_type') === 'CUSTOMER') {
        AuthService.logout();
        return;
      }

      return profileData.data;
    } catch (error: any) {
      AnalyticsService.sendEvent(EVENTS.ERROR_OCCURRED, {
        error,
      }, true);
    }
  };

  userPreferenceSettingsRequest = async () => {
    try {
      const response = await FetchStore.request<
        'USER_SETTINGS',
        UserPreferenceSettings
      >(
        'USER_SETTINGS',
        '/userpreference/settings',
        'USER_PREFERENCE_SETTINGS',
      );

      if (response.success) {
        if (typeof response.data?.saveChartsToServer === 'boolean') {
          AnalyticsService.sendEvent(EVENTS.CHART_SAVE_USER_PREFERENCE, {
            preference: response.data.saveChartsToServer ? 'cloudStorage' : 'localStorage',
          }, true);
        }

        this.setUserPreferenceSettings(response.data);
      }

      throw new Error(response?.error?.message);
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      LoggerService.log('USER', error?.message);
    }
  };

  saveUserPreferenceSettings = async (
    preferenceSettings: Partial<UserPreferenceSettings>,
  ) => {
    try {
      const response = await FetchStore.request<
        'USER_SETTINGS'
      >(
        'USER_SETTINGS',
        '/userpreference/settings',
        'SAVE_USER_PREFERENCE_SETTINGS',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: {
            data: preferenceSettings,
          },
        },
      );

      if (response.success) {
        if (typeof preferenceSettings.saveChartsToServer === 'boolean') {
          AnalyticsService.sendEvent(EVENTS.CHART_SAVE_CONFIRMED_OPTION, {
            preference: preferenceSettings.saveChartsToServer ? 'cloudStorage' : 'localStorage',
          }, true);
        }

        this.setUserPreferenceSettings({
          ...this.userPreferenceSettings,
          ...preferenceSettings,
        } as UserPreferenceSettings);

        return true;
      }

      throw new Error(response?.error?.message);
    } catch (error: any) {
      return Promise.reject(error);
    }
  }

  private handleSuccessGetProfileResponse = (
    userProfile: ProfileResponse,
  ) => {
    const { ucc, type, customerStatus } = userProfile.userInfo;

    LoggerService.log('USER', `Profile fetched for user: ${ucc}`);

    this.setUserData(userProfile);
    this.setCustomerStatus(customerStatus);
    this.setUserType(type);
    AnalyticsService.sendEvent(EVENTS.LOGGED_IN, {}, true);

    CleverTapService.login(
      `${userProfile.userInfo.firstName} ${userProfile.userInfo.lastName}`,
      this.profileId || '',
      userProfile.userInfo.ucc,
      userProfile.userInfo.email,
      userProfile.userInfo.mobile,
      userProfile.userInfo.type,
      this.isFnOEnabled(userProfile.exchangesEnabled),
      this.isCommoditiesEnabled(userProfile.exchangesEnabled),
    );
  };

  handleLeadUserAction = () => {
    RoutingService.goToExternalRoute('OPEN_ACCOUNT', { target: '_blank' });
  };

  isFnOEnabled = (segmentsEnabled: SegmentType[]) =>
    segmentsEnabled.some((segment) =>
      Validation.isFnO(segment) ||
      Validation.isCurrency(segment),
    );

  isSecuritiesEnabled = (segmentsEnabled: SegmentType[]) =>
    segmentsEnabled.some((segment) =>
      Validation.isEquities(segment) ||
      Validation.isFnO(segment) ||
      Validation.isCurrency(segment),
    );

  isCommoditiesEnabled = (segmentsEnabled: SegmentType[]) =>
    segmentsEnabled.some((segment) => Validation.isCommodity(segment));

  // get absa status only for EQUITIES_EXCHANGE
  getPaymentModes = () =>
    this.segmentList
      ?.filter(
        userSegment =>
          userSegment?.paymentMode &&
          userSegment?.paymentModeActivationStatus,
      )
      ?.map(({ paymentMode, paymentModeActivationStatus }) => ({
        paymentMode,
        paymentModeActivationStatus,
      }));

  isAsbaUser = () =>
    !!this.getPaymentModes()?.find(({ paymentMode }) => paymentMode === 'ASBA');

  isAsbaStatusOf = (
    modeStatus: PaymentMode,
    activationStatus: PaymentModeActivationStatus,
  ) => {
    const paymentModes = this.getPaymentModes();
    if (paymentModes) {
      return paymentModes.some(
        ({ paymentMode, paymentModeActivationStatus }) =>
          paymentMode === modeStatus &&
          paymentModeActivationStatus === activationStatus,
      );
    }

    return false;
  };

  isAsbaRegistrationPending = () =>
    this.isAsbaStatusOf('WALLET', 'ASBA_REGISTRATION_PENDING');

  isAsbaDeregistrationPending = () =>
    this.isAsbaStatusOf('ASBA', 'ASBA_DEREGISTRATION_PENDING');

  public isOrderingBlockedByASBA = (segment?: SegmentType) => {
    const isNotCommodity = !Validation.isCommodity(segment);
    if (this.isAsbaRegistrationPending()) {
      return {
        isBlocked: isNotCommodity,
        asbaErrorMessage: LocaleService.translate(
          'ASBA_REGISTRATION_PENDING_ERROR_MESSAGE',
        ),
      };
    }
    if (this.isAsbaDeregistrationPending()) {
      return {
        isBlocked: isNotCommodity,
        asbaErrorMessage: LocaleService.translate(
          'ASBA_DEREGISTRATION_PENDING_ERROR_MESSAGE',
        ),
      };
    }
    return {
      isBlocked: false,
      asbaErrorMessage: '',
    };
  };

  checkLeadUsers = () => {
    if (!this.isActiveUser) {
      AuthService.logoutRedirect(true);
      return true;
    }
  };
}

const userService = new UserService();

autorun(() => {
  const { profileId, profile, ucc } = userService;
  if (ucc) {
    AnalyticsService.setUserData({
      ucc,
      profileId,
      mobile: profile?.mobile,
    });
  }
});

export default userService;
